import {createExpandedRowModel, stockFeatures, tableFeatures, useTable} from '@tanstack/react-table-v9'
import {useCallback, useMemo} from 'react'

import {MIN_CELL_WIDTH} from '../../components/table/constants'
import type {TableDataType} from '../../components/table/table-data-type'
import {isRowSelectable} from '../../components/table/use-row-selection'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import type {ColumnModel} from '../../models/column-model'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'
import {getRowId} from './helpers/get-row-id'
import type {ColumnDef, Row} from './helpers/types'
import {type ColumnDefV9, memexTableFeature, type RowV9} from './helpers/types-v9'
import {useColumnResizingV9} from './hooks/use-column-resizing-v9'
import {useColumnSizing} from './hooks/use-column-sizing'
import {controlledStateErrorWarnings, useControlledTableState} from './hooks/use-controlled-table-state'
import {RowDraggerColumnDef} from './providers/columns/row-dragger-column-def'
import {useTableColumns} from './providers/columns/use-table-columns'
import {getCustomGroupedRowModelV9} from './row-models/get-custom-grouped-row-model-v9'

export type TableProps = {
  fields: ReadonlyArray<ColumnModel>
  pinnedRightColumnDefs?: ReadonlyArray<ColumnDef<TableDataType>>
}

const features = tableFeatures({
  ...stockFeatures,
  memexTableFeature,
  expandedRowModel: createExpandedRowModel(),
  groupedRowModel: getCustomGroupedRowModelV9(),
})

export function useReactTableV9({fields, pinnedRightColumnDefs}: TableProps) {
  const readonlyData = useReactTableData()
  // Replace with a mutable array while maintaining referential equality checks.
  const data = useMemo(() => [...readonlyData], [readonlyData])
  const {columnDefinitions, columnVisibility} = useTableColumns()
  const {columnResizing, onColumnResizingChange} = useColumnResizingV9()
  const {columnSizing, onColumnSizingChange} = useColumnSizing()
  const {hierarchyEnabled} = useTableHierarchy()

  const pinnedLeftColumnDefs: ReadonlyArray<ColumnDef<TableDataType>> = useMemo(() => [RowDraggerColumnDef], [])
  const allColumnDefs = useMemo(
    () => [...pinnedLeftColumnDefs, ...columnDefinitions, ...(pinnedRightColumnDefs ?? [])],
    [columnDefinitions, pinnedLeftColumnDefs, pinnedRightColumnDefs],
  ) as unknown as Array<ColumnDefV9<TableDataType>>

  const {expanded, grouping, groupQueryData, sorting, subIssuesQueryData} = useControlledTableState()
  const {copilotTasksEnabled} = subIssuesQueryData
  const columnOrder = useMemo(
    () => [
      ...pinnedLeftColumnDefs.map(def => def.id),
      ...fields.map(field => field.id.toString()),
      ...(pinnedRightColumnDefs?.map(def => def.id) ?? []),
    ],
    [fields, pinnedLeftColumnDefs, pinnedRightColumnDefs],
  )

  const getRowCanExpand = useCallback(
    (row: RowV9<TableDataType>) => {
      if (row.getIsGrouped()) return true
      if (!hierarchyEnabled && !copilotTasksEnabled) return false
      if (hierarchyEnabled) {
        const subIssuesProgress = row.original.getSubIssuesProgress()
        if (subIssuesProgress && subIssuesProgress.total > 0) return true
      }
      return copilotTasksEnabled && row.subRows.some(subRow => subRow.agentAssignmentTaskData)
    },
    [hierarchyEnabled, copilotTasksEnabled],
  )
  const getRowCanSelect = useCallback(
    (row: RowV9<TableDataType>) => isRowSelectable(row as unknown as Row<TableDataType>),
    [],
  )

  const table = useTable({
    features,
    columns: allColumnDefs,
    data,
    getRowId,
    getRowCanExpand: hierarchyEnabled || copilotTasksEnabled ? getRowCanExpand : undefined,
    enableRowSelection: getRowCanSelect,
    manualSorting: true,
    columnResizeMode: 'onChange',
    defaultColumn: {minSize: MIN_CELL_WIDTH},
    autoResetAll: false,
    groupedColumnMode: false,
    initialState: {
      columnPinning: {
        start: pinnedLeftColumnDefs.map(def => def.id),
        end: pinnedRightColumnDefs?.map(def => def.id) ?? [],
      },
      groupQueryData,
      subIssuesQueryData,
    },
    state: {
      columnVisibility,
      columnOrder,
      columnSizing,
      columnResizing,
      expanded,
      grouping,
      groupQueryData,
      sorting,
      subIssuesQueryData,
    },
    onColumnSizingChange,
    onColumnResizingChange,
    ...controlledStateErrorWarnings,
  })

  const getState = useCallback(() => table.store.state, [table.store])
  return useMemo(() => ({...table, getState}), [getState, table])
}

function useReactTableData() {
  const {items} = useMemexItems()
  return useMemo(() => items, [items])
}
